import React, {
    FC,
    useState,
    useCallback,
    Ref,
    useRef,
    useImperativeHandle,
    useEffect,
} from 'react';
import * as THREE from 'three';

import { ThreeEvent, useThree } from '@react-three/fiber';

import mergeRefs from 'merge-refs';
import { OutlineEdge } from '../meshes/OutlineEdge';
import { Html } from '@react-three/drei';
// import { useDrag, useHover } from '@use-gesture/react';

export interface InferShape {
    scrollToView: () => void;
}
interface RedactShapes {
    startPoint: THREE.Vector3;
    endPoint: THREE.Vector3;
    onErase?: () => void;
    textureRef?: Ref<THREE.MeshStandardMaterial>;
    edgeRef: Ref<THREE.Mesh>;
    ref?: React.Ref<InferShape>;
    // onDragging?: (cond: boolean) => void;
}

const getSizeFromTwoVec = (
    startPoint: THREE.Vector3,
    endPoint: THREE.Vector3,
    aspect: number
): THREE.Vector3 => {
    let size = new THREE.Vector3()
        .subVectors(startPoint, endPoint)
        .multiplyScalar(aspect);
    if (size.x === 0 && size.y === 0) {
        size = new THREE.Vector3()
            .subVectors(endPoint, startPoint)
            .multiplyScalar(aspect);
    } else if (size.x === 0 || Number.isSafeInteger(size.x) === false) {
        size = new THREE.Vector3()
            .subVectors(
                new THREE.Vector3(endPoint.x, startPoint.y, 0),
                new THREE.Vector3(startPoint.x, endPoint.y)
            )
            .multiplyScalar(aspect);
    } else if (size.y === 0 || Number.isSafeInteger(size.y) === false) {
        size = new THREE.Vector3()
            .subVectors(
                new THREE.Vector3(startPoint.x, endPoint.y, 0),
                new THREE.Vector3(endPoint.x, startPoint.y)
            )
            .multiplyScalar(aspect);
    }
    return size;
};
const Shapes: FC<RedactShapes> = React.forwardRef(
    (
        {
            startPoint,
            endPoint,
            onErase,
            textureRef,
            edgeRef,
            // onDragging
        },
        ref
    ) => {
        // const sizeCanvas = useThree((state) => state.size);

        const camera = useThree((state) => state.camera);
        // const [redactWidth, setRedactWidth] = useState<number>();
        // const [redactHeight, setRedactHeight] = useState<number>();
        // const [redactPosition, setRedactPosition] = useState<THREE.Vector3>();
        const [isHavingEdges, setHavingEdges] = useState<boolean>(true);
        const htmlElement = React.useRef<HTMLDivElement | null>(null);

        const getLocalMeshPosition = useCallback(
            (vecDraft: THREE.Vector3): THREE.Vector3 => {
                const pos = new THREE.Vector3(); // create once and reuse
                const vec = new THREE.Vector3();
                vec.set(vecDraft.x, vecDraft.y, vecDraft.z);
                vec.unproject(camera);

                vec.sub(camera.position).normalize();

                const distance = -camera.position.z / vec.z;

                return pos
                    .copy(camera.position)
                    .add(vec.multiplyScalar(distance));
            },
            [camera]
        );

        const getInitValue = useCallback(
            (startPoint: THREE.Vector3, endPoint: THREE.Vector3) => {
                const localStartMesh = getLocalMeshPosition(startPoint);
                const localEndMesh = getLocalMeshPosition(endPoint);

                const size = getSizeFromTwoVec(localStartMesh, localEndMesh, 2);

                const centeroid = new THREE.Vector3().addVectors(
                    localStartMesh,
                    localEndMesh
                );
                const center = centeroid;
                const planeWidth = Math.abs(size.x);
                const planeHeight = Math.abs(size.y);
                // meshBox.current?.position.copy(center);

                return {
                    position: new THREE.Vector3(center.x, center.y, 1),
                    height: planeHeight,
                    width: planeWidth,
                };
                // setRedactPosition(new THREE.Vector3(center.x, center.y, 1));
                // setRedactHeight(planeHeight);
                // setRedactWidth(planeWidth);
                // setRedactDefaultPosition(new THREE.Vector3(center.x, center.y, 1));
                // setRedactDefaultHeight(planeHeight);
                // setRedactDefaultWidth(planeWidth);
            },
            [getLocalMeshPosition]
        );

        /**
         * handler when onlick above element
         */
        const handleEraseRedact = useCallback(
            (event: ThreeEvent<MouseEvent>) => {
                event.stopPropagation();
                setHavingEdges(false);
                document.body.style.cursor = 'auto';
                if (typeof onErase === 'function') onErase();
            },
            [onErase]
        );
        const testeref = useRef<THREE.Mesh | null>(null);

        /**
         * stop update matrix value when the graphic is rendered, the issue due to glitch in webgl
         */
        const stopupdate = React.useCallback(() => {
            if (testeref.current !== null) {
                testeref.current.matrixAutoUpdate = false;
                testeref.current.matrixWorldAutoUpdate = false;
            }
        }, []);
        useImperativeHandle(
            ref,
            () => ({
                scrollToView: () => {
                    htmlElement.current?.scrollIntoView({
                        block: 'center',
                        inline: 'center',
                        behavior: 'smooth',
                    });
                },
            }),
            []
        );
        const [hovered, setHovered] = useState(false);

        useEffect(() => {
            document.body.style.cursor = hovered ? 'pointer' : 'auto';
        }, [hovered]);

        return (
            <mesh
                position={getInitValue(startPoint, endPoint).position}
                onClick={handleEraseRedact}
                onPointerOver={() => setHovered(true)}
                onPointerOut={() => setHovered(false)}
            >
                <planeGeometry
                    attach="geometry"
                    args={[
                        getInitValue(startPoint, endPoint).width as number,
                        getInitValue(startPoint, endPoint).height as number,
                    ]}
                />
                <meshStandardMaterial
                    ref={textureRef}
                    color="red"
                    opacity={0}
                    attach="material"
                    emissiveIntensity={0.2}
                    roughness={0.8}
                    metalness={0.9}
                    bumpScale={0.05}
                    transparent
                    side={THREE.DoubleSide}
                />
                <Html
                    as="div" // Wrapping element (default: 'div')
                >
                    <div ref={htmlElement} />
                </Html>
                <OutlineEdge
                    isDrawed
                    matrixAutoUpdate
                    matrixWorldAutoUpdate
                    onAfterRender={stopupdate}
                    ref={mergeRefs(edgeRef, testeref)}
                    scale={isHavingEdges ? 1.1 : 0}
                    threshold={15} // Display edges only when the angle between two faces exceeds this value (default=15 degrees)
                    color="purple"
                />
            </mesh>
        );
    }
);

export default Shapes;
